import React, { useCallback } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import { updateSendingEmailSchema } from "@common/schemas/sending-email.schema";
import type { SendingEmailWithDomain } from "@api/sendingEmail.api";
import { useUpdateSendingEmail } from "@api/sendingEmail.api";
import { useWorkspaceSendingOwner } from "@hooks/useWorkspaceSendingOwner";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { validateFormikWithZodSchema } from "@helpers/form";
import { HOTKEYS_SCOPES } from "@hooks/keyboard/useKeyboardCommand";
import { SendingEmailEntitiesField } from "./SendingEmailEntitiesField";
import { useSendingEmailEntityMapping } from "./useSendingEmailEntityMapping";

const editSendingEmailFormSchema = z.object({
    displayName: z.string(),
    entityIds: z.array(z.number()),
});

type EditSendingEmailFormValues = z.infer<typeof editSendingEmailFormSchema>;

const fields = createFormikFields<EditSendingEmailFormValues>();

interface EditSendingEmailModalProps {
    show: boolean;
    email: SendingEmailWithDomain | null;
    onClose: () => void;
}

export const EditSendingEmailModal: React.FC<EditSendingEmailModalProps> = ({
    show,
    email,
    onClose,
}) => {
    // Only mount the form once the workspace entities have loaded: the form
    // seeds its initial entity selection by mapping the saved `entityUuids` to
    // ids, and an empty (still-loading) entity list would seed an empty
    // selection that a save then persists as a full replacement — silently
    // clearing existing assignments.
    const { isReady } = useSendingEmailEntityMapping();

    return (
        <PlainModal show={show} onHide={onClose}>
            {show && email && isReady && (
                <EditSendingEmailForm email={email} onClose={onClose} />
            )}
        </PlainModal>
    );
};

interface EditSendingEmailFormProps {
    email: SendingEmailWithDomain;
    onClose: () => void;
}

const EditSendingEmailForm: React.FC<EditSendingEmailFormProps> = ({
    email,
    onClose,
}) => {
    const owner = useWorkspaceSendingOwner();
    const updateEmail = useUpdateSendingEmail(owner);
    const { toast } = useToaster();
    const { entityIdsToUuids, entityUuidsToIds } =
        useSendingEmailEntityMapping();

    const formik = useFormik<EditSendingEmailFormValues>({
        initialValues: {
            displayName: email.displayName ?? "",
            entityIds: entityUuidsToIds(email.entityUuids),
        },
        validate: validateFormikWithZodSchema(editSendingEmailFormSchema),
        validateOnMount: true,
        onSubmit: async (values, helpers) => {
            const entityUuids = entityIdsToUuids(values.entityIds);

            try {
                await updateEmail.mutateAsync({
                    emailId: email.id,
                    body: updateSendingEmailSchema.parse({
                        displayName: values.displayName.trim() || null,
                        entityUuids,
                    }),
                });
                toast("Sender email updated");
                onClose();
            } catch {
                helpers.setSubmitting(false);
            }
        },
    });

    const handleClose = useCallback(() => {
        formik.resetForm();
        onClose();
    }, [formik, onClose]);

    return (
        <FormikProvider value={formik}>
            <Form>
                <StandardModalHeader>Edit sender email</StandardModalHeader>
                <VStack gap={16}>
                    <fields.Text
                        name="displayName"
                        label="Display name"
                        optional
                        placeholder="Acme Billing"
                        labelTooltip="Shown as the sender name in the recipient's inbox."
                        size="xl"
                    />
                    <SendingEmailEntitiesField />
                    <HStack justify="end" gap={4}>
                        <Button
                            variant="secondary"
                            size="md"
                            type="button"
                            onClick={handleClose}
                        >
                            Cancel
                        </Button>
                        <ButtonWithLoader
                            variant="primary"
                            size="md"
                            type="submit"
                            hotKey="mod+enter"
                            hotKeyOptions={{
                                description: "Save sender email",
                                preventDefault: true,
                                enableOnFormTags: true,
                                scopes: [HOTKEYS_SCOPES.SETTINGS_MODAL],
                            }}
                            hotKeyDisplayPosition="inline"
                            loading={formik.isSubmitting}
                            disabled={!formik.isValid}
                        >
                            Save
                        </ButtonWithLoader>
                    </HStack>
                </VStack>
            </Form>
        </FormikProvider>
    );
};
